<template>
  <div class="windowed-call-stage fixed-full">
    <!-- Primary Remote Window -->
    <ParticipantWindow
      v-if="showRemoteWindow"
      :participant="primaryRemoteParticipantMemo"
      :zIndex="remoteZ"
      :initialPos="remotePos"
      :is-caller-pre-answer="isCallerPreAnswer"
      :call-status="callStatus"
      :remote-tracks-changed-at="remoteTrackTick"
      @focus="bringToFront('remote')"
      @hangup="$emit('hangup', 'remote')"
    />

    <!-- Secondary Remote Windows -->
    <ParticipantWindow
      v-for="(participant, index) in secondaryRemoteParticipants"
      :key="`remote-secondary-${participant.key}`"
      :show="showSecondaryRemoteWindowEl(participant.key)"
      :participant="participant"
      :zIndex="getSecondaryRemoteZ(participant)"
      :initialPos="getSecondaryRemotePos(index)"
      :is-caller-pre-answer="isCallerPreAnswer"
      :call-status="callStatus"
      :remote-tracks-changed-at="remoteTrackTick"
      @focus="bringToFrontSecondary(participant.key)"
      @hangup="$emit('hangup', 'remote')"
    />

    <!-- Local Window -->
    <ParticipantWindow
      v-show="props.showLocalWindow"
      :participant="localParticipantMemo"
      isLocal
      :initialPos="localPos"
      :initialWidth="260"
      :zIndex="localZ"
      :video-on="props.videoOnLocal"
      :local-ready="props.localReady"
      @focus="bringToFront('local')"
      :class="{ 'menu-open': localMenuOpen, 'z-top': localMenuOpen }"
    >
      <template #header-left>
        <div class="header-left">
          <q-select
            style="max-width: 130px"
            v-model="selectedAvatarIdModel"
            :options="avatarOptionsWithUpload"
            option-label="label"
            option-value="id"
            emit-value
            map-options
            dense
            clearable
            :loading="avatarsLoading"
            placeholder="Avatar"
            class="no-drag"
            popup-content-class="avatar-select-popup"
          >
            <template #option="scope">
              <q-separator v-if="scope.opt.__separator" inset spaced />
              <q-item
                v-else-if="scope.opt.__upload"
                clickable
                @click.stop.prevent="openUploadPicker"
              >
                <q-item-section avatar>
                  <q-icon name="file_upload" />
                </q-item-section>
                <q-item-section>
                  <q-item-label>Upload custom avatar…</q-item-label>
                </q-item-section>
              </q-item>
              <q-item v-else v-bind="scope.itemProps">
                <q-item-section>
                  <q-item-label>{{ scope.opt.label }}</q-item-label>
                </q-item-section>
                <q-item-section v-if="isRemovableAvatar(scope.opt)" side class="no-drag">
                  <q-btn
                    dense
                    flat
                    round
                    icon="delete"
                    color="negative"
                    @click.stop.prevent="onRemoveAvatar(scope.opt)"
                  />
                </q-item-section>
              </q-item>
            </template>
          </q-select>
          <q-select
            style="max-width: 126px"
            v-model="expressionPresetModel"
            :options="expressionPresetOptions"
            option-label="label"
            option-value="value"
            emit-value
            map-options
            dense
            class="no-drag hidden"
            popup-content-class="avatar-select-popup"
            placeholder="Motion"
          />
          <input
            ref="uploadInput"
            type="file"
            accept="image/*"
            style="display: none"
            @change="onUploadFileChange"
          />
        </div>
      </template>

      <template #controls>
        <ActionMenu
          size="xs"
          flat
          round
          dense
          buttonColor="white"
          menuContentClass="windowed-action-menu"
          :close-signal="menuCloseToken"
          :showFullscreen="false"
          :showResize="false"
          :layerVisibility="layerVisibility"
          :segmentationEnabled="segmentationEnabled"
          :avatarHasFaceRig="avatarHasFaceRig"
          :isFullscreen="isFullscreen"
          :hasAvatar="hasAvatar"
          @action="onLocalAction"
          @open:background-picker="$emit('open:background-picker')"
          @open:eye-settings="$emit('open:eye-settings')"
          @open:refine-selection="$emit('open:refine-selection')"
          @open:voice-settings="() => (showVoiceDialog = true)"
          @open="() => (localMenuOpen = true)"
          @close="() => (localMenuOpen = false)"
        />
      </template>

      <template #footer>
        <div class="footer-stack">
          <div v-if="isCallerPreAnswer" class="call-status-footer">
            <div class="call-status__text q-gutter-sm">
              <div class="col q-mb-sm">
                <UserAvatarBadge :src="primaryRemoteAvatar" size="sm" />
              </div>
              <div>{{ preAnswerStatusTitle }}</div>
              <div class="ringing-icon">
                <q-icon
                  name="call"
                  color="positive"
                  size="28px"
                  class="q-my-md animate__animated animate__infinite animate__pulse"
                />
              </div>
              <div class="col-12 text-caption">
                Now’s a good time to setup your avatar, or your friends will suffer your ugly face
                😝
              </div>
            </div>
          </div>

          <div class="bottom-controls">
            <q-btn
              dense
              flat
              round
              :icon="mutedLocal ? 'mic_off' : 'mic'"
              @click="$emit('mute-toggle', 'local')"
            />
            <q-btn
              dense
              flat
              round
              icon="call_end"
              color="negative"
              @click="$emit('hangup', 'local')"
            />
            <q-btn
              dense
              flat
              round
              :icon="videoOnLocal ? 'videocam' : 'videocam_off'"
              @click="$emit('toggle-video', 'local')"
            />
          </div>
        </div>
      </template>
    </ParticipantWindow>

    <!-- Local dialogs rendered alongside the floating windows so they work even if AvatarChatDialog is hidden -->
    <!-- Eyes Settings Dialog -->
    <q-dialog v-model="showEyesDialog" content-class="z-max">
      <q-card flat bordered class="q-pa-sm" style="min-width: 280px; max-width: 460px">
        <EyesSettings v-model:eye="eyeModel" v-model:irisStyle="irisModel" />
        <q-card-actions align="right">
          <q-btn flat label="Close" @click="showEyesDialog = false" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Background Picker Dialog -->
    <BackgroundPickerDialog
      v-model="showBackgroundDialog"
      :selectedId="selectedBackgroundId"
      @select="(p) => emit('background-selected', p)"
    />

    <!-- Voice Settings Dialog -->
    <q-dialog v-model="showVoiceDialog" content-class="z-max">
      <q-card flat bordered class="q-pa-sm" style="min-width: 300px; max-width: 700px">
        <VoiceSettings v-model:voice="voiceModel" />
        <q-card-actions align="right">
          <q-btn flat label="Close" @click="showVoiceDialog = false" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
  import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
  import DraggableWindow from './DraggableWindow.vue'
  import ParticipantWindow from './ParticipantWindow.vue'
  import UserAvatarBadge from '@/components/ui/badges/UserAvatarBadge.vue'
  import ActionMenu from './common/ActionMenu/PluginActionMenu.vue'
  import EyesSettings from './EyesSettings.vue'
  import BackgroundPickerDialog from './common/BackgroundPicker/BackgroundPickerDialog.vue'
  import VoiceSettings from './common/VoiceSettings/VoiceSettings.vue'

  const props = defineProps({
    localStream: { type: Object, default: null },
    remoteStream: { type: Object, default: null },
    remoteParticipants: { type: Array, default: () => [] },
    localTitle: { type: String, default: '' },
    remoteTitle: { type: String, default: 'Remote' },
    localInitial: { type: Object, default: () => ({ x: 20, y: 20 }) },
    remoteInitial: { type: Object, default: () => ({ x: 340, y: 20 }) },
    remoteName: { type: String, default: '' },
    remoteAvatar: { type: String, default: '' },
    mutedLocal: { type: Boolean, default: false },
    mutedRemote: { type: Boolean, default: false },
    videoOnLocal: { type: Boolean, default: true },
    videoOnRemote: { type: Boolean, default: true },
    // When true, always render a window per remote participant (presence-first)
    // even if media isn't available yet or uid is temporarily missing.
    forceShowAllRemotes: { type: Boolean, default: true },
    // passthrough props for ActionMenu
    layerVisibility: { type: Object, default: () => ({}) },
    segmentationEnabled: { type: Boolean, default: false },
    avatarHasFaceRig: { type: Boolean, default: false },
    isFullscreen: { type: Boolean, default: false },
    hasAvatar: { type: Boolean, default: false },
    // Call context
    sessionRole: { type: String, default: 'caller' }, // 'caller' | 'callee'
    callStatus: { type: String, default: 'ringing' }, // 'ringing' | 'connecting' | 'active' | 'ended'
    preAnswerTitle: { type: String, default: '' },
    showLocalWindow: { type: Boolean, default: true },
    // Avatar selection for local window (dropdown)
    avatarOptions: { type: Array, default: () => [] },
    selectedAvatarId: { type: String, default: '' },
    avatarsLoading: { type: Boolean, default: false },
    expressionPresetOptions: { type: Array, default: () => [] },
    selectedExpressionPreset: { type: String, default: 'expressive' },
    // Optional models for local dialogs (when AvatarChatDialog is hidden)
    eye: { type: Object, default: () => ({}) },
    irisStyle: { type: String, default: 'vector' },
    selectedBackgroundId: { type: String, default: '' },
    // Voice settings model (for local dialog)
    voiceSettings: { type: Object, default: () => ({}) },
    // Gate local video reveal until upstream processing is ready
    localReady: { type: Boolean, default: true },
    // Track remote track changes (e.g., camera closed) to force UI updates
    remoteTracksChangedAt: { type: [Object, Number], default: null }
  })

  const emit = defineEmits([
    'mute-toggle',
    'hangup',
    'toggle-video',
    'open-action',
    'action',
    'open:background-picker',
    'open:eye-settings',
    'open:voice-settings',
    'open:refine-selection',
    'upload:avatar',
    'remove:avatar',
    // Model updates for local dialogs
    'update:eye',
    'update:irisStyle',
    'background-selected',
    'update:selectedAvatarId',
    'update:selectedExpressionPreset',
    'update:voiceSettings'
  ])

  const showEyesDialog = ref(false)
  const showBackgroundDialog = ref(false)
  const showVoiceDialog = ref(false)
  const eyeModel = computed({
    get: () => props.eye || {},
    set: (v) => emit('update:eye', v || {})
  })
  const irisModel = computed({
    get: () => props.irisStyle,
    set: (v) => emit('update:irisStyle', v)
  })
  const voiceModel = computed({
    get: () => (props.voiceSettings ? { ...props.voiceSettings } : {}),
    set: (v) => emit('update:voiceSettings', v || {})
  })
  const avatarOptionsWithUpload = computed(() => {
    const base = Array.isArray(props.avatarOptions) ? props.avatarOptions : []
    const items = [...base]
    if (items.length > 0) items.push({ id: '__sep__', __separator: true })
    items.push({ id: '__upload__', __upload: true, label: 'Upload custom avatar…' })
    return items
  })
  const uploadInput = ref(null)

  function openUploadPicker() {
    try {
      uploadInput.value?.click?.()
    } catch {
      /* ignore */
    }
  }

  function onUploadFileChange(e) {
    try {
      const files = e?.target?.files || []
      const file = files[0]
      if (file) emit('upload:avatar', file)
    } catch {
      /* ignore */
    }
    try {
      // reset input so selecting the same file again still triggers change
      if (e && e.target) e.target.value = ''
    } catch {
      /* ignore */
    }
  }
  function isRemovableAvatar(opt) {
    try {
      if (!opt || typeof opt !== 'object') return false
      const id = String(opt.id || '')
      if (!id || id.startsWith('character:')) return false
      // Prefer explicit kind flag from LocalAssetStore
      if (opt.kind && String(opt.kind) !== 'avatar') return false
      return true
    } catch {
      return false
    }
  }

  function onRemoveAvatar(opt) {
    try {
      const id = String(opt?.id || '')
      if (!id) return
      emit('remove:avatar', id)
      // Close the menu after click to provide feedback
      try {
        menuCloseToken.value++
      } catch {
        /* ignore */
      }
    } catch {
      /* ignore */
    }
  }

  // v-model for header-left avatar selector
  const selectedAvatarIdModel = computed({
    get: () => props.selectedAvatarId || '',
    set: (v) => emit('update:selectedAvatarId', v || '')
  })
  const expressionPresetModel = computed({
    get: () => String(props.selectedExpressionPreset || 'expressive'),
    set: (v) => emit('update:selectedExpressionPreset', String(v || 'expressive'))
  })

  // Avatar dropdown removed from UI; keep props/emits for compatibility if needed

  const localMenuOpen = ref(false)
  const menuCloseToken = ref(0)
  const secondaryRemoteZByKey = ref({})
  // Local dialogs (work even when AvatarChatDialog is hidden)

  // Memoize primary remote participant to avoid creating new objects each render
  // This ensures ParticipantWindow receives stable prop references
  const primaryRemoteParticipantMemo = computed(() => {
    return {
      uid: props.remoteParticipants?.[0]?.uid || '',
      name: String(props.remoteParticipants?.[0]?.name || props.remoteName || '').trim(),
      avatar: String(props.remoteParticipants?.[0]?.avatar || props.remoteAvatar || '').trim(),
      stream: props.remoteParticipants?.[0]?.stream || props.remoteStream || null
    }
  })

  // Memoize local participant to avoid creating new objects each render
  const localParticipantMemo = computed(() => ({
    stream: props.localStream,
    name: String(props.localTitle || '').trim()
  }))

  const isCallerPreAnswer = computed(() => {
    const status = String(props.callStatus || '').toLowerCase()
    // Strictly only for ringing/connecting
    if (status !== 'ringing' && status !== 'connecting') return false
    return true
  })
  const remoteParticipants = computed(() => {
    const rows = Array.isArray(props.remoteParticipants) ? props.remoteParticipants : []
    const normalized = rows
      .map((row, index) => {
        const uid = String(row?.uid || '').trim()
        const key = uid || String(row?.key || '').trim() || `remote-${index}`
        return {
          key,
          uid,
          stream: row?.stream || null,
          name: String(row?.name || '').trim(),
          avatar: String(row?.avatar || '').trim()
        }
      })
      .filter((row) => !!row.key)
    if (normalized.length) return normalized
    const fallbackName = String(props.remoteName || '').trim()
    const shouldUseFallback =
      isCallerPreAnswer.value || !!props.remoteStream || fallbackName.length > 0
    if (!shouldUseFallback) return []
    return [
      {
        key: 'remote-primary',
        uid: '',
        stream: props.remoteStream || null,
        name: fallbackName,
        avatar: String(props.remoteAvatar || '').trim()
      }
    ]
  })
  const primaryRemoteParticipant = computed(() => remoteParticipants.value[0] || null)
  const secondaryRemoteParticipants = computed(() => {
    const list = Array.isArray(remoteParticipants.value) ? remoteParticipants.value.slice(1) : []
    // Keep secondary participants even if their camera is off; body will handle visibility.
    return list
  })
  const primaryRemoteStream = computed(() => primaryRemoteParticipant.value?.stream || null)
  const primaryRemoteName = computed(
    () => primaryRemoteParticipant.value?.name || String(props.remoteName || '').trim()
  )
  const primaryRemoteAvatar = computed(
    () => primaryRemoteParticipant.value?.avatar || String(props.remoteAvatar || '').trim()
  )
  const defaultPreAnswerTitle = computed(() => {
    const name = String(primaryRemoteName.value || props.remoteTitle || '').trim()
    return name ? `Calling ${name}` : 'Calling...'
  })
  const preAnswerStatusTitle = computed(() => {
    const explicit = String(props.preAnswerTitle || '').trim()
    if (explicit) return explicit
    return defaultPreAnswerTitle.value
  })
  const remoteWindowTitle = computed(() => {
    if (isCallerPreAnswer.value) {
      const name = String(primaryRemoteName.value || props.remoteTitle || 'Remote').trim()
      return name ? `Calling ${name}` : 'Calling...'
    }
    return String(primaryRemoteName.value || props.remoteTitle || 'Remote').trim()
  })
  // React to remote track state changes pushed from composable
  const remoteTrackTick = computed(() => {
    const val = props.remoteTracksChangedAt
    const ts = val && typeof val === 'object' ? val.value : val
    const tick = Number(ts || 0)
    return tick
  })
  const hasActivePeer = computed(() => {
    if (isCallerPreAnswer.value || props.callStatus === 'connecting') return true
    const hasPrimary = !!primaryRemoteParticipant.value
    const anySecondary = (secondaryRemoteParticipants.value || []).length > 0
    return hasPrimary || anySecondary
  })
  const showRemoteWindow = computed(() => hasActivePeer.value)

  const showSecondaryRemoteWindow = computed(() => {
    const force = !!props.forceShowAllRemotes
    return (secondaryRemoteParticipants.value || []).map((p) => ({
      key: p.key,
      show: force ? true : !!p.uid
    }))
  })
  const showSecondaryRemoteWindowEl = (key) => {
    const list = showSecondaryRemoteWindow.value || []
    const item = list.find((i) => i.key === key)
    return item ? item.show : false
  }
  const secondaryRemoteWindowTitle = (participant) => {
    const name = String(participant?.name || '').trim()
    if (isCallerPreAnswer.value) {
      if (name) return `Calling ${name}`
      return 'Calling...'
    }
    return name || props.remoteTitle
  }

  // z-index stacking management so clicked window comes to front
  const zCounter = ref(100)
  const localZ = ref(++zCounter.value)
  const remoteZ = ref(++zCounter.value)
  const ensureSecondaryZ = (key) => {
    const normalizedKey = String(key || '').trim()
    if (!normalizedKey) return ++zCounter.value
    const existing = Number(secondaryRemoteZByKey.value?.[normalizedKey] || 0)
    if (existing > 0) return existing
    const next = ++zCounter.value
    secondaryRemoteZByKey.value = {
      ...(secondaryRemoteZByKey.value || {}),
      [normalizedKey]: next
    }
    return next
  }
  const getSecondaryRemoteZ = (participant) => ensureSecondaryZ(participant?.key)
  const bringToFrontSecondary = (key) => {
    const normalizedKey = String(key || '').trim()
    if (!normalizedKey) return
    secondaryRemoteZByKey.value = {
      ...(secondaryRemoteZByKey.value || {}),
      [normalizedKey]: ++zCounter.value
    }
    if (localMenuOpen.value) {
      try {
        menuCloseToken.value++
      } catch {
        void 0
      }
      localMenuOpen.value = false
    }
  }

  function bringToFront(which) {
    const next = ++zCounter.value
    if (which === 'local') localZ.value = next
    else if (which === 'remote') remoteZ.value = next
    if (localMenuOpen.value) {
      try {
        menuCloseToken.value++
      } catch {
        void 0
      }
      localMenuOpen.value = false
    }
  }

  const remotePos = ref({ x: props.remoteInitial.x || 340, y: props.remoteInitial.y || 20 })
  const localPos = ref({ x: props.localInitial.x || 20, y: props.localInitial.y || 20 })
  const getSecondaryRemotePos = (index) => ({
    x: Number(remotePos.value?.x || 340) + (Number(index) + 1) * 34,
    y: Number(remotePos.value?.y || 20) + (Number(index) + 1) * 26
  })

  onMounted(() => {
    try {
      const rw = 420
      const rh = 200
      const lw = 260
      const lh = 200
      const cx = Math.max(0, Math.round((window.innerWidth - rw) / 2))
      const cy = Math.max(0, Math.round((window.innerHeight - rh) / 2))
      remotePos.value = { x: cx, y: cy }
      // local slightly offset to bottom-right of remote
      const lx = Math.min(
        Math.max(12, cx + Math.round(rw / 2)),
        Math.max(12, window.innerWidth - lw - 12)
      )
      const ly = Math.min(
        Math.max(12, cy + Math.round(rh / 2)),
        Math.max(12, window.innerHeight - lh - 12)
      )
      localPos.value = { x: lx, y: ly }
    } catch {
      void 0
    }
  })
  onUnmounted(() => {
    secondaryRemoteZByKey.value = {}
  })

  // Robust local ActionMenu handler (mirrors TestingPanel behavior)
  function onLocalAction(evt) {
    try {
      const key = evt?.key || evt?.event || evt?.id || ''
      // Always forward generic action for parent listeners
      emit('action', { ...evt, target: 'local' })
      const lower = String(key || '').toLowerCase()
      try {
        console.log('[WindowedCallStage] local onAction', { key, lower, evt })
      } catch {
        void 0
      }

      // Only explicit dialog-open keys should open dialogs.
      // Layer checkbox keys like `layer-eyes` must not open settings dialogs.
      if (lower === 'eye-settings' || lower === 'eye_settings') {
        emit('open:eye-settings')
        showEyesDialog.value = true
        return
      }
      if (lower === 'add-background' || lower === 'background' || lower === 'add_bg') {
        emit('open:background-picker')
        showBackgroundDialog.value = true
        return
      }
      if (lower === 'voice-settings' || lower === 'voice_settings' || lower === 'voice') {
        emit('open:voice-settings')
        showVoiceDialog.value = true
        return
      }
    } catch {
      void 0
    }
  }

  // Legacy computeds removed: visibility handled by frame monitor + hide-body
</script>

<style scoped>
  .windowed-call-stage {
    /* handled by Quasar utility classes: fixed-full */
    pointer-events: none;
    /* Above app page layers, under Quasar menus/dialogs */
    z-index: 5500;
  }
  .windowed-call-stage > * {
    pointer-events: auto;
  }
  .bottom-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    width: 100%;
  }
  .footer-stack {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    gap: 6px;
  }
  .ringing-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }
  .call-status-footer {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: rgba(255, 255, 255, 0.9);
    font-weight: 600;
  }
  .call-status__text {
    text-align: center;
    line-height: 1.25;
  }
  .windowed-call-stage .remote-window,
  .windowed-call-stage .local-window {
    pointer-events: auto;
  }
  .media-vessel {
    position: relative;
    width: 100%;
    height: 100%;
    background: #000;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .video-el {
    width: 100%;
    height: 100%;
    object-fit: cover;
    background: #000;
  }
  /* No decode-hiding: keep remote <video> visible and rely on overlay */
  .no-video {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #000;
    color: rgba(255, 255, 255, 0.85);
    font-size: 12px;
  }
  .call-status {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #fff;
    font-size: 14px;
  }
  .call-status__text {
    font-weight: 600;
  }
  .header-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }
</style>
